import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getUsers, createUser, updateUser, resetUserPassword, deactivateUser, reactivateUser, getRoles, inviteUser } from '../api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import InviteLinkDialog from '../components/InviteLink';
import { inviteActionLabel } from '../utils/emailLinks';
import { Plus, Edit, Key, UserX, UserCheck, Shield, Users as UsersIcon, Eye, Mail } from 'lucide-react';

// New users get an emailed invite by default and choose their own password.
const EMPTY_FORM = { first_name: '', last_name: '', alias: '', email: '', password: '', role_id: '', send_invite: true };

function Users() {
  const navigate = useNavigate();
  const { hasPermission, user: currentUser, startImpersonation, isImpersonating } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [deactivateConfirm, setDeactivateConfirm] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [formData, setFormData] = useState(EMPTY_FORM);
  // Invite outcome: a short confirmation when it was emailed, or the link
  // to copy when email is not set up on the server.
  const [notice, setNotice] = useState('');
  const [inviteLink, setInviteLink] = useState(null);
  const [invitingId, setInvitingId] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [usersRes, rolesRes] = await Promise.all([getUsers(), getRoles()]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
    } catch (err) { setError('Failed to load users'); }
    finally { setLoading(false); }
  };

  const showInviteOutcome = (invite, person, kind) => {
    if (!invite) return;
    if (invite.url) {
      setInviteLink({ url: invite.url, name: person.name, email: person.email, kind });
    } else if (invite.sent) {
      setNotice(kind === 'reset' ? `Password reset link sent to ${person.email}.` : `Invite sent to ${person.email}.`);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    const { send_invite, password, ...fields } = formData;
    const payload = send_invite ? { ...fields, send_invite: true } : { ...fields, password };
    try {
      const res = await createUser(payload);
      setShowCreateModal(false);
      setFormData(EMPTY_FORM);
      const created = res.data || {};
      showInviteOutcome(created.invite, {
        name: created.name || [fields.first_name, fields.last_name].filter(Boolean).join(' '),
        email: created.email || fields.email,
      }, 'invite');
      loadData();
    } catch (err) { setError(err.response?.data?.error || 'Failed to create user'); }
  };

  const handleInvite = async (u) => {
    const kind = u.invited_at ? 'invite' : 'reset';
    setError('');
    setNotice('');
    setInvitingId(u.id);
    try {
      const res = await inviteUser(u.id);
      showInviteOutcome(res.data?.invite, u, kind);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || (kind === 'reset' ? 'Failed to send the password reset link' : 'Failed to send the invite'));
    } finally {
      setInvitingId(null);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await updateUser(editUser.id, { first_name: editUser.first_name, last_name: editUser.last_name, alias: editUser.alias, email: editUser.email, role_id: editUser.role_id });
      setEditUser(null);
      loadData();
    } catch (err) { setError(err.response?.data?.error || 'Failed to update user'); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    try {
      await resetUserPassword(resetPasswordUser.id, newPassword);
      setResetPasswordUser(null);
      setNewPassword('');
      alert('Password reset successfully');
    } catch (err) { setError(err.response?.data?.error || 'Failed to reset password'); }
  };

  const handleDeactivate = async () => {
    try {
      await deactivateUser(deactivateConfirm);
      setDeactivateConfirm(null);
      loadData();
    } catch (err) { setError(err.response?.data?.error || 'Failed to deactivate user'); }
  };

  const handleReactivate = async (userId) => {
    try {
      await reactivateUser(userId);
      loadData();
    } catch (err) { setError(err.response?.data?.error || 'Failed to reactivate user'); }
  };

  const handleImpersonate = async (userId) => {
    try {
      await startImpersonation(userId);
      navigate('/');
    } catch (err) { setError(err.response?.data?.error || 'Failed to impersonate user'); }
  };

  if (!hasPermission('manage_users')) {
    return <div className="alert alert-error">You do not have permission to manage users.</div>;
  }

  if (loading) return <div className="loading-container"><div className="spinner"></div></div>;

  return (
    <div className="users-page">
      <div className="page-header">
        <h1 className="page-title">Users</h1>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}><Plus size={18} /> New User</button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-success" role="status">{notice}</div>}

      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th style={{ width: 220 }}></th></tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr><td colSpan="5"><div className="empty-state"><UsersIcon size={48} className="empty-state-icon" /><h3 className="empty-state-title">No users found</h3></div></td></tr>
              ) : (
                users.map(u => (
                  <tr key={u.id}>
                    <td>{u.name} {u.alias && <span className="text-sm text-secondary ml-1">“{u.alias}”</span>} {u.id === currentUser.id && <span className="badge badge-primary ml-2">You</span>}</td>
                    <td>{u.email}</td>
                    <td>{u.role_name || '-'}</td>
                    <td>
                      <span className={`badge ${u.active ? 'badge-success' : 'badge-gray'}`}>{u.active ? 'Active' : 'Inactive'}</span>
                      {u.invited_at && <span className="badge badge-warning ml-2" title="Invite sent; they have not set a password yet">Invited</span>}
                    </td>
                    <td>
                      <button className="btn btn-icon" onClick={() => setEditUser({ ...u })} title="Edit"><Edit size={16} /></button>
                      <button className="btn btn-icon" onClick={() => navigate(`/users/${u.id}/permissions`)} title="Permissions"><Shield size={16} /></button>
                      <button className="btn btn-icon" onClick={() => setResetPasswordUser(u)} title="Reset Password"><Key size={16} /></button>
                      {u.active && (
                        <button className="btn btn-icon" onClick={() => handleInvite(u)} disabled={invitingId === u.id} title={inviteActionLabel(u)} aria-label={`${inviteActionLabel(u)} to ${u.name}`}><Mail size={16} /></button>
                      )}
                      {!isImpersonating && u.id !== currentUser.id && u.active && u.role_name !== 'Admin' && (
                        <button className="btn btn-icon" onClick={() => handleImpersonate(u.id)} title="View as this user"><Eye size={16} /></button>
                      )}
                      {u.id !== currentUser.id && (
                        u.active ? (
                          <button className="btn btn-icon" onClick={() => setDeactivateConfirm(u.id)} title="Deactivate"><UserX size={16} /></button>
                        ) : (
                          <button className="btn btn-icon" onClick={() => handleReactivate(u.id)} title="Reactivate"><UserCheck size={16} /></button>
                        )
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create User Modal */}
      <Modal isOpen={showCreateModal} onClose={() => { setShowCreateModal(false); setFormData(EMPTY_FORM); }} title="New User">
        <form onSubmit={handleCreate}>
          <div className="modal-body">
            <div className="grid grid-cols-2">
              <div className="form-group">
                <label className="form-label">First name *</label>
                <input type="text" className="form-input" value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} required autoComplete="off" />
              </div>
              <div className="form-group">
                <label className="form-label">Last name</label>
                <input type="text" className="form-input" value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} autoComplete="off" />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Alias</label>
              <input type="text" className="form-input" value={formData.alias} onChange={(e) => setFormData({ ...formData, alias: e.target.value })} placeholder="What people call them, if not their first name" autoComplete="off" />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="new-user-email">Email *</label>
              <input id="new-user-email" type="email" className="form-input" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
            </div>
            <div className="form-group">
              <label className="checkbox-wrapper">
                <input type="checkbox" className="checkbox" checked={formData.send_invite} onChange={(e) => setFormData({ ...formData, send_invite: e.target.checked })} />
                Email an invite instead of setting a password
              </label>
              {formData.send_invite && (
                <p className="text-sm text-secondary" style={{ margin: '4px 0 0' }}>They get a link to choose their own password.</p>
              )}
            </div>
            {!formData.send_invite && (
              <div className="form-group">
                <label className="form-label" htmlFor="new-user-password">Password *</label>
                <input id="new-user-password" type="password" className="form-input" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required minLength="6" />
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Role</label>
              <select className="form-select" value={formData.role_id} onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}>
                <option value="">Select Role</option>
                {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary">{formData.send_invite ? 'Create and send invite' : 'Create User'}</button>
          </div>
        </form>
      </Modal>

      {/* Edit User Modal */}
      <Modal isOpen={!!editUser} onClose={() => setEditUser(null)} title="Edit User">
        {editUser && (
          <form onSubmit={handleUpdate}>
            <div className="modal-body">
              <div className="grid grid-cols-2">
                <div className="form-group">
                  <label className="form-label">First name *</label>
                  <input type="text" className="form-input" value={editUser.first_name || ''} onChange={(e) => setEditUser({ ...editUser, first_name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Last name</label>
                  <input type="text" className="form-input" value={editUser.last_name || ''} onChange={(e) => setEditUser({ ...editUser, last_name: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Alias</label>
                <input type="text" className="form-input" value={editUser.alias || ''} onChange={(e) => setEditUser({ ...editUser, alias: e.target.value })} placeholder="What people call them, if not their first name" />
              </div>
              <div className="form-group">
                <label className="form-label">Email *</label>
                <input type="email" className="form-input" value={editUser.email} onChange={(e) => setEditUser({ ...editUser, email: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <select className="form-select" value={editUser.role_id || ''} onChange={(e) => setEditUser({ ...editUser, role_id: e.target.value })}>
                  <option value="">Select Role</option>
                  {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setEditUser(null)}>Cancel</button>
              <button type="submit" className="btn btn-primary">Save</button>
            </div>
          </form>
        )}
      </Modal>

      {/* Reset Password Modal */}
      <Modal isOpen={!!resetPasswordUser} onClose={() => { setResetPasswordUser(null); setNewPassword(''); }} title="Reset Password">
        {resetPasswordUser && (
          <form onSubmit={handleResetPassword}>
            <div className="modal-body">
              <p className="mb-4">Reset password for <strong>{resetPasswordUser.name}</strong></p>
              <div className="form-group">
                <label className="form-label">New Password *</label>
                <input type="password" className="form-input" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength="6" />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => { setResetPasswordUser(null); setNewPassword(''); }}>Cancel</button>
              <button type="submit" className="btn btn-primary">Reset Password</button>
            </div>
          </form>
        )}
      </Modal>

      <InviteLinkDialog result={inviteLink} onClose={() => setInviteLink(null)} />

      <ConfirmDialog isOpen={!!deactivateConfirm} onClose={() => setDeactivateConfirm(null)} onConfirm={handleDeactivate} title="Deactivate User" message="Deactivate this user? They will no longer be able to log in." confirmText="Deactivate" />
    </div>
  );
}

export default Users;
